import Text from 'components/commons/Text';
import LocalizeEditor from 'components/input/LocalizeEditor';
import LocalizeInput from 'components/input/LocalizeInput';
import UploadButton from 'components/input/UploadButton';
import { InnerSection } from 'components/layouts/CommonForm';
import { InnerGroup } from 'components/layouts/CommonForm';
import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { Button, Row, useModelStateContext } from 'saladsoft-primitive';
import { AdminPromotionDto, AdminPromotionRequestDto } from 'services/data-contracts';
import ServiceHelper from 'stores/ServiceHelper';
import { useStoreWrapper } from 'stores/StoreHelper';

const PromotionForm = ({
  promotion,
  onSave,
}: {
  promotion?: AdminPromotionDto;
  onSave: () => void;
}) => {
  const { setState, setMultipleState, stateModel, unmountModel } =
    useModelStateContext<AdminPromotionDto>();
  const { uiStore } = useStoreWrapper();

  const [image, setImage] = React.useState<File | null>(null);

  const handleClickCreate = () => {
    if (promotion?.id) {
      ServiceHelper?.adminPromotionService
        ?.updatePromotion(promotion?.id, stateModel as AdminPromotionRequestDto)
        .then(async (res) => {
          if (res.status === 200) {
            const promotionId = res.data.id;
            if (promotionId && image) {
              await deleteImages();
              ServiceHelper?.adminPromotionService
                ?.createPromotionImage(promotionId, { seq: 1 }, { file: image! })
                .then((res) => {
                  closeModal('프로모션 수정이 완료되었습니다.');
                  onSave();
                });
            } else {
              closeModal('프로모션 수정이 완료되었습니다.');
              onSave();
            }
          } else {
            toast.error('프로모션 수정에 실패하였습니다.');
          }
        });
    } else {
      if (stateModel) {
        const newState = { ...stateModel, isActive: true };
        ServiceHelper.adminPromotionService?.createPromotion(newState).then((res) => {
          if (res.status === 200) {
            const promotionId = res.data.id;
            if (promotionId && image) {
              ServiceHelper?.adminPromotionService
                ?.createPromotionImage(promotionId, { seq: 1 }, { file: image! })
                .then((res) => {
                  closeModal('프로모션 생성이 완료되었습니다.');
                  onSave();
                });
            } else {
              closeModal('프로모션 생성이 완료되었습니다.');
              onSave();
            }
          } else {
            toast.error('프로모션 생성에 실패하였습니다.');
          }
        });
      }
    }
  };

  const closeModal = (title: string) => {
    toast.success(title);
    uiStore?.modal.confirm();
    onSave();
  };

  const deleteImages = async () => {
    if (promotion?.images && promotion.images.length > 0) {
      const deletePromises = promotion.images.map((image) =>
        ServiceHelper?.adminPromotionService?.deletePromotionImage(
          promotion.id as number,
          image.id as number,
        ),
      );
      await Promise.all(deletePromises);
    }
    return;
  };

  useEffect(() => {
    if (promotion) {
      setMultipleState(promotion);
    } else {
      setState('isExpire', false);
    }
    return () => {
      unmountModel();
    };
  }, [promotion]);
  return (
    <div style={{ padding: 20 }}>
      <InnerSection>
        <InnerGroup>
          <div>
            <Text type="H3">프로모션 타이틀</Text>
            <Text type="H4">프로모션 타이틀을 입력 해주세요.</Text>
            <Text
              buttonRole="DESCRIPTION"
              style={{ fontSize: 12, color: 'var(--point-color)', marginTop: 2 }}
            >
              * 모든 입력값은 필수입니다.
            </Text>
            <div style={{ marginTop: 50 }}>
              <UploadButton
                onChange={(file) => {
                  setImage(file);
                }}
              >
                사진등록
              </UploadButton>
            </div>
          </div>
          <div>
            <LocalizeInput
              defaultValue={promotion?.localizedTitle}
              onChange={(value) => setState('localizedTitle', value)}
            />
          </div>
        </InnerGroup>
      </InnerSection>
      <InnerSection>
        <InnerGroup>
          <div>
            <Text type="H3">주소</Text>
            <Text type="H4">프로모션과 연관된 주소를 입력 해주세요.</Text>
            <Text
              buttonRole="DESCRIPTION"
              style={{ fontSize: 12, color: 'var(--point-color)', marginTop: 2 }}
            >
              * 모든 입력값은 필수입니다.
            </Text>
          </div>
          <div>
            <LocalizeInput
              defaultValue={promotion?.localizedAddress}
              onChange={(value) => setState('localizedAddress', value)}
            />
          </div>
        </InnerGroup>
      </InnerSection>

      <InnerSection>
        <InnerGroup>
          <div>
            <Text type="H3">프로모션 기간</Text>
            <Text type="H4">프로모션 기간을 입력 해주세요. 기간은 자유롭게 입력 가능합니다.</Text>
            <Text
              buttonRole="DESCRIPTION"
              style={{ fontSize: 12, color: 'var(--point-color)', marginTop: 2 }}
            >
              * 모든 입력값은 필수입니다.
            </Text>
          </div>
          <div>
            <LocalizeInput
              defaultValue={promotion?.localizedPeriod}
              onChange={(value) => setState('localizedPeriod', value)}
            />
          </div>
        </InnerGroup>
      </InnerSection>
      <InnerSection>
        <InnerGroup>
          <div>
            <Text type="H3">프로모션 상세</Text>
            <Text type="H4">프로모션 상세 내용을 입력 해주세요.</Text>
            <Text
              buttonRole="DESCRIPTION"
              style={{ fontSize: 12, color: 'var(--point-color)', marginTop: 2 }}
            >
              * 모든 입력값은 필수입니다.
            </Text>
          </div>
          <div>
            <LocalizeEditor
              defaultValue={promotion?.localizedDesc}
              onChange={(value) => setState('localizedDesc', value)}
            />
          </div>
        </InnerGroup>
      </InnerSection>
      <Row justifyContent="flex-end" gap={10}>
        <Button
          type="outlined"
          style={{ marginTop: 20 }}
          onClick={async () => {
            uiStore?.modal.close();
          }}
        >
          취소
        </Button>
        <Button
          style={{ marginTop: 20 }}
          onClick={async () => {
            if (stateModel) {
              handleClickCreate();
            }
          }}
        >
          {promotion?.id ? '프로모션 수정' : '프로모션 추가'}
        </Button>
      </Row>
    </div>
  );
};

export default PromotionForm;
